/**
 * Runtime brand. The platform is multi-tenant: brand comes from the workspace
 * (GET /api/workspace/brand), resolved by the current host. The build-time env
 * BRAND is only the fallback before the fetch resolves (or if it fails).
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { BRAND as ENV_BRAND } from './brand';

export interface Brand {
  name: string;
  wordmarkLead: string;
  wordmarkTail: string;
  accent: string | null;
  logoUrl: string | null;
  faviconUrl: string | null;
  supportEmail: string | null;
}

const fallback: Brand = {
  name: ENV_BRAND.name,
  wordmarkLead: ENV_BRAND.wordmarkLead,
  wordmarkTail: ENV_BRAND.wordmarkTail,
  accent: null,
  logoUrl: null,
  faviconUrl: null,
  supportEmail: null,
};

const BrandContext = createContext<Brand>(fallback);
export const useBrand = () => useContext(BrandContext);

export function BrandProvider({ children }: { children: ReactNode }) {
  const [brand, setBrand] = useState<Brand>(fallback);

  useEffect(() => {
    let alive = true;
    // Resolve by host so a custom-domain / subdomain tenant gets its own brand.
    const host = typeof window !== 'undefined' ? window.location.hostname : '';
    fetch(`/api/workspace/brand?domain=${encodeURIComponent(host)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!alive || !data?.brand) return;
        const b = data.brand;
        setBrand({
          name: b.name ?? fallback.name,
          wordmarkLead: b.wordmarkLead ?? fallback.wordmarkLead,
          wordmarkTail: b.wordmarkTail ?? fallback.wordmarkTail,
          accent: b.accent ?? null,
          logoUrl: b.logoUrl ?? null,
          faviconUrl: b.faviconUrl ?? null,
          supportEmail: b.supportEmail ?? null,
        });
        // Apply favicon + accent at runtime.
        if (b.faviconUrl) {
          let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
          if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
          link.href = b.faviconUrl;
        }
        if (b.name) document.title = b.name;
        if (b.accent) document.documentElement.style.setProperty('--brand-accent', b.accent);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  return <BrandContext.Provider value={brand}>{children}</BrandContext.Provider>;
}
